/**
 * App Protection API Service
 *
 * Service for managing Intune App Protection (MAM) policies via backend API.
 * Uses snake_case field naming to match backend Pydantic models.
 */

import { ApiService } from './api';
import type {
  AndroidAppProtectionRequest,
  IosAppProtectionRequest,
  AppProtectionPolicyResponse,
  AppProtectionPolicyListResponse,
} from '../types/appProtection';

export class AppProtectionApiService {
  /**
   * Create Android App Protection policy
   */
  static async createAndroidPolicy(
    tenantId: string,
    request: AndroidAppProtectionRequest
  ): Promise<AppProtectionPolicyResponse> {
    return ApiService.request<AppProtectionPolicyResponse>(
      `/api/tenants/${tenantId}/app-protection/android`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  /**
   * Create iOS App Protection policy
   */
  static async createIosPolicy(
    tenantId: string,
    request: IosAppProtectionRequest
  ): Promise<AppProtectionPolicyResponse> {
    return ApiService.request<AppProtectionPolicyResponse>(
      `/api/tenants/${tenantId}/app-protection/ios`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  }

  /**
   * List all App Protection policies for tenant
   */
  static async getPolicies(
    tenantId: string
  ): Promise<AppProtectionPolicyListResponse> {
    return ApiService.request<AppProtectionPolicyListResponse>(
      `/api/tenants/${tenantId}/app-protection/policies`
    );
  }

  /**
   * Delete App Protection policy
   */
  static async deletePolicy(
    tenantId: string,
    platform: string,
    policyId: string
  ): Promise<{ success: boolean; message: string }> {
    return ApiService.request<{ success: boolean; message: string }>(
      `/api/tenants/${tenantId}/app-protection/${platform}/${policyId}`,
      {
        method: 'DELETE',
      }
    );
  }
}
